import {DEFAULT, SINGLE_RELATIONSHIP_TYPE} from "./shared-types";

export class RawDataAttributes {
    time_stamp: string;
    value: number;
}

export class RawDataRelationships {
    series: SINGLE_RELATIONSHIP_TYPE = DEFAULT.SINGLE;
}

export class RawData {
    attributes: RawDataAttributes;
    relationships: RawDataRelationships;
    type: 'raw_data';
    id: string;

    constructor(seriesId?: string, timeStamp?: string, value?: number) {
        this.attributes = new RawDataAttributes();
        this.relationships = new RawDataRelationships();
        this.relationships.series.data = {id: seriesId, type: 'series'};
        this.attributes.time_stamp = timeStamp;
        this.attributes.value = value;
    }
}

/** A single upsert entry for the bulk endpoint. `id` is omitted for creates. */
export interface RawDataBulkUpsert {
    id?: string;
    value: number;
    time_stamp: string;
    series_id: string;
}

/**
 * A single delete entry for the bulk endpoint.
 *
 * The cell, not the row id, is the durable address of a point: a collector that
 * re-inserts a point gives it a new id, so `id` is only a hint about the row the
 * sheet last saw there.
 */
export interface RawDataBulkDelete {
    id?: string;
    series_id: string;
    time_stamp: string;
}

/** Request body sent to POST /api/raw_data/bulk. */
export interface RawDataBulkRequest {
    data: {
        type: 'raw_data_bulk';
        attributes: {
            upserts: RawDataBulkUpsert[];
            deletes: RawDataBulkDelete[];
        };
    };
}

/** A fully-hydrated raw_data record returned in the bulk response. */
export interface RawDataBulkUpserted {
    id: string;
    value: number;
    time_stamp: string;
    series_id: string;
    is_calculated: boolean;
    created_by: string;
    created_on: string;
    changed_by: string;
    changed_on: string;
}

/** A single per-item failure returned by the partial-success bulk endpoint. */
export interface RawDataBulkFailure {
    op: 'upsert' | 'delete';
    /** Position in the original request array (upserts or deletes). */
    index: number;
    id: string | null;
    series_id: string | null;
    time_stamp: string | null;
    /** HTTP status code for this item (400 | 403 | 404 | 422 | 500). */
    status: number;
    title: string;
    detail: string;
}

/**
 * One cleared cell returned by the bulk endpoint.
 *
 * `id` is whichever row was removed, which need not be the id the client sent,
 * so a caller matches its queue on the series and timestamp instead.
 */
export interface RawDataBulkDeleted {
    id: string | null;
    series_id: string | null;
    time_stamp: string | null;
}

/** Response body returned by POST /api/raw_data/bulk. */
export interface RawDataBulkResponse {
    data: {
        type: 'raw_data_bulk';
        attributes: {
            upserted: RawDataBulkUpserted[];
            deleted: RawDataBulkDeleted[];
            deleted_ids: string[];
            failures: RawDataBulkFailure[];
        };
    };
}
